/**
 * services/quick-replies.js — account-shared canned replies expandable via
 * /slash commands in the composer.
 *
 * Data model (per account, v1):
 *   { id, shortcut, text, updatedAt, _pending? }
 *
 * - id:        server-assigned `qr_{16hex}` once created. During optimistic
 *              insert the row carries a local `tmp_{base36ts}_{4rand}` id
 *              and the `_pending: true` flag; the flag clears when the
 *              server response lands and the tempId is replaced with the
 *              real server id in the store.
 * - shortcut:  normalized token without the leading slash, lowercased,
 *              [a-z0-9_] only, max 24 chars
 * - text:      expansion body; any string; newlines allowed
 * - updatedAt: unix ms, sorted most-recent first in Settings
 * - _pending:  transient flag (client-only) — true between optimistic
 *              commit and server reconcile. Edit/delete are blocked on
 *              pending rows; see updateQuickReply/removeQuickReply below.
 *
 * Sync strategy:
 *   Server is source of truth (Supabase inbox_quick_replies). localStorage
 *   is an instant-render cache so Composer never waits on network. On mount
 *   we hydrate from cache → fetch fresh → overwrite cache with authoritative
 *   state. On every CRUD we flip local state first (optimistic), then fire
 *   the server call in the background; a server failure rolls back the
 *   local change and toasts.
 *
 * Task #106 note: ids are now server-assigned via POST /quick-replies,
 * and updates/deletes go through PATCH/DELETE /quick-replies/{id} which
 * scope by session.account_id in the WHERE clause. This replaces the
 * old client-generated id + upsert pattern that was vulnerable to
 * cross-account overwrite.
 *
 * First-run migration: if the new cache key (`inbox_v2_quick_replies_v2_{accountId}`)
 * is empty but the pre-sync v1 key (`inbox_v2_quick_replies_{accountId}`) has
 * rows, we upload them as fresh server entries so agents don't lose their
 * previously-local-only work. One-shot, gated by a success sentinel.
 *
 * Future scope: per-agent libraries (add a `scope` column + agent_email,
 * see #97 and Phase 4.7c).
 */

import { atom } from 'nanostores';
import { getAccountId, $session } from '../stores/session.js';
import * as api from './api.js';
import { isPermissionDeniedError, markDegraded, markHealthy } from './permission-degraded.js';

// ── Reactive store ──────────────────────────────────────────────────────────
// Array of quick-reply objects, sorted most-recently-updated first. Consumers
// (Settings UI + Composer match) subscribe via useStore($quickReplies).
export const $quickReplies = atom([]);

// ── localStorage IO ─────────────────────────────────────────────────────────
// v2 cache key separated from the pre-sync v1 key so we can detect the
// one-shot migration cleanly. Sentinel below guards against re-upload.
function _storageKey(accountId) {
  return `inbox_v2_quick_replies_v2_${accountId || 'anon'}`;
}
function _legacyStorageKey(accountId) {
  return `inbox_v2_quick_replies_${accountId || 'anon'}`;
}
function _migrationSentinelKey(accountId) {
  return `inbox_v2_quick_replies_migrated_${accountId || 'anon'}`;
}

function _load(accountId) {
  if (!accountId) return [];
  try {
    const raw = localStorage.getItem(_storageKey(accountId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function _save(accountId, list) {
  if (!accountId) return;
  try {
    localStorage.setItem(_storageKey(accountId), JSON.stringify(list || []));
  } catch { /* quota */ }
}

/**
 * One-shot migration: if the v1 cache (pre-sync) has rows and we've never
 * migrated for this account, push them to the server as fresh entries.
 * Logs to console so the agent can see what happened in devtools if they
 * look. Errors don't block the hydrate flow — worst case a few local-only
 * rows are lost, which matches the pre-sync behavior.
 */
async function _migrateLegacyCache(accountId) {
  if (!accountId) return;
  const sentinel = _migrationSentinelKey(accountId);
  try {
    if (localStorage.getItem(sentinel) === '1') return; // already done
  } catch { return; }

  let legacy = [];
  try {
    const raw = localStorage.getItem(_legacyStorageKey(accountId));
    if (raw) legacy = JSON.parse(raw);
    if (!Array.isArray(legacy) || legacy.length === 0) {
      localStorage.setItem(sentinel, '1'); // nothing to migrate, seal it
      return;
    }
  } catch { return; }

  console.log('[v2 quick-replies] migrating', legacy.length, 'legacy local replies to server');
  let migrated = 0;
  for (const row of legacy) {
    try {
      // Post #106: legacy ids are discarded and the server assigns a new
      // one. That's fine — the legacy cache only existed before any sync,
      // so no other device references these ids. Each legacy row becomes
      // a fresh server-owned row keyed off its shortcut.
      await api.createQuickReply({
        shortcut: row.shortcut,
        text:     row.text,
      });
      migrated += 1;
    } catch (e) {
      // Conflicts are ok — they mean another device already synced this
      // shortcut. Skip and continue; the server copy wins.
      console.warn('[v2 quick-replies] migration skip', row.shortcut, e.message);
    }
  }
  try { localStorage.setItem(sentinel, '1'); } catch {}
  console.log('[v2 quick-replies] ✓ migrated', migrated, '/', legacy.length);
}

// #170 — personal (scope='agent') sorted ahead of shared (scope='account')
// so on duplicate shortcut both still render in the /slash dropdown but the
// agent's own version is the active/highlighted match by default.
// Within each tier, most-recently-updated first.
function _sorted(list) {
  return [...list].sort((a, b) => {
    const aPersonal = (a.scope || 'account') === 'agent' ? 0 : 1;
    const bPersonal = (b.scope || 'account') === 'agent' ? 0 : 1;
    if (aPersonal !== bPersonal) return aPersonal - bPersonal;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });
}

/**
 * Hydrate the store for the current account in two phases:
 *   1. Sync: instant paint from localStorage cache
 *   2. Async: fetch fresh from server + overwrite cache with authoritative state
 *
 * Both hydrations are safe to call on every account switch. Errors on the
 * server fetch are swallowed — the cache stays in place so UI still works
 * offline (viewing + composer expansion still work; CRUD will hit the
 * server when it comes back).
 */
export function hydrateQuickReplies() {
  const accountId = getAccountId();
  // Phase 1: instant from cache (decorate with #128 cached norm)
  $quickReplies.set(_sorted(_load(accountId).map(_withCachedNorm)));
  if (!accountId) return Promise.resolve();

  // Phase 2: server refresh + optional one-shot migration
  return (async () => {
    try {
      await _migrateLegacyCache(accountId);
      const raw = await api.fetchQuickReplies();
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      // Normalize server shape → store shape: server uses created_at/updated_at
      // as ISO strings; store uses updatedAt (ms) for sort comparison.
      // _normShortcut cached at hydrate time so the typing-path conflict
      // check (#128) doesn't re-normalize every row on every keystroke.
      const normalized = list.map(r => ({
        id:            r.id,
        shortcut:      r.shortcut,
        _normShortcut: normalizeShortcut(r.shortcut),
        text:          r.text,
        // #170 — scope: 'account' (admin-shared) | 'agent' (caller's personal).
        // Defaults to 'account' for legacy rows that haven't been re-fetched
        // since the migration so existing slash-expansion still works during
        // the transition.
        scope:         r.scope || 'account',
        agentEmail:    r.agent_email || null,
        updatedAt:     r.updated_at ? Date.parse(r.updated_at) : Date.now(),
      }));
      $quickReplies.set(_sorted(normalized));
      _save(accountId, normalized);
      markHealthy('quick-replies');
    } catch (e) {
      console.warn('[v2 quick-replies] server hydrate failed:', e.message);
      // #168 — surface 403 as degraded-permissions hint (the proxy
      // gate-checks /quick-replies same as everything else, so a
      // 403 here means session.permissions is missing crm.live_chat.ALL).
      if (isPermissionDeniedError(e)) {
        markDegraded('quick-replies', e.message);
      }
    }
  })();
}

$session.listen((session) => {
  // Account switched — instant paint from the new account's cache, then
  // fire the async server hydrate in the background.
  const accountId = session?.accountId || null;
  $quickReplies.set(_sorted(_load(accountId).map(_withCachedNorm)));
  if (accountId) hydrateQuickReplies();
});

hydrateQuickReplies();

// ── Shortcut validation ────────────────────────────────────────────────────
/**
 * Normalize a user-entered shortcut to the stored form. Strips a leading
 * slash if present, lowercases, drops non [a-z0-9_] characters, caps at 24.
 * Callers pass the result through to the object literal.
 */
export function normalizeShortcut(raw) {
  if (!raw) return '';
  return String(raw)
    .replace(/^\/+/, '')        // drop leading slash(es)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '') // alnum + underscore
    .slice(0, 24);
}

/**
 * Is the shortcut already taken by a different reply? Case-insensitive via
 * normalization. Returns true if a conflict exists. Used by the Settings
 * form to prevent duplicates.
 *
 * Perf (#128): each row carries a cached `_normShortcut` set lazily by
 * `_withCachedNorm` so we don't re-normalize every row on every keystroke.
 * The cache is invalidated whenever the row's shortcut changes (CRUD
 * helpers below set `_normShortcut` to undefined alongside any shortcut
 * mutation). Falls back to live normalization if the cache is missing.
 */
function _shortcutConflict(list, shortcut, excludeId = null, scope = 'agent') {
  const norm = normalizeShortcut(shortcut);
  if (!norm) return false;
  // #170 — collisions only count WITHIN the same scope. The agent's
  // personal `/hi` is allowed to coexist with the admin's shared `/hi`
  // (the dropdown shows both); we only block another personal `/hi` for
  // the same agent or another shared `/hi` across the account.
  for (let i = 0; i < list.length; i++) {
    const r = list[i];
    if (r.id === excludeId) continue;
    if ((r.scope || 'account') !== scope) continue;
    const cached = r._normShortcut !== undefined ? r._normShortcut : normalizeShortcut(r.shortcut);
    if (cached === norm) return true;
  }
  return false;
}

/** Decorate a quick-reply row with a cached normalized shortcut so the
 * typing-path conflict check skips the normalize() work. Called whenever
 * a row enters the store (load / create / update). */
function _withCachedNorm(row) {
  if (!row) return row;
  if (row._normShortcut !== undefined) return row;
  return { ...row, _normShortcut: normalizeShortcut(row.shortcut) };
}

// ── CRUD ────────────────────────────────────────────────────────────────────

// Optimistic-only id used between "user hit Save" and "server response
// lands". Never sent to the server — the proxy's POST /quick-replies
// route ignores any id in the body and assigns its own. The tmp_ prefix
// lets other helpers recognize a row that hasn't been persisted yet
// (see the _isTempId check in updateQuickReply/removeQuickReply below,
// which blocks edit/delete on in-flight creates).
function _newTempId() {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 6);
  return `tmp_${t}_${r}`;
}

function _isTempId(id) {
  return typeof id === 'string' && id.startsWith('tmp_');
}

/**
 * Add a new quick reply. Flow:
 *   1. Generate a local tempId (`tmp_*`) and optimistic-commit with
 *      `_pending: true` so the UI paints immediately.
 *   2. POST to the server WITHOUT an id — the proxy assigns its own.
 *   3. On success, reconcile: replace the optimistic row (matched by
 *      tempId) with the server row (which carries the real `qr_*` id,
 *      authoritative timestamps, etc.), clearing `_pending`.
 *   4. On failure, rollback the optimistic commit and throw.
 *
 * During the in-flight window (step 1 → 3), the row is rendered with
 * `_pending: true`. The QuickReplies Settings screen hides Edit/Delete
 * on pending rows; updateQuickReply/removeQuickReply also hard-guard
 * against tmp_* ids as a belt-and-braces safety net.
 *
 * Returns the final store row (with server id) once reconciled.
 */
export async function addQuickReply({ shortcut, text, scope = 'agent' }) {
  const norm = normalizeShortcut(shortcut);
  if (!norm)         throw new Error('Shortcut is required');
  if (!text?.trim()) throw new Error('Reply text is required');
  const before = $quickReplies.get();
  // #170 — conflict check is per-scope. A personal /hi is allowed even when
  // a shared /hi exists; we only block another personal /hi or another
  // shared /hi (within the same scope).
  if (_shortcutConflict(before, norm, null, scope)) {
    throw new Error(`"/${norm}" is already in use ${scope === 'account' ? 'as a shared reply' : 'in your personal replies'}`);
  }

  const tempId = _newTempId();
  const optimistic = {
    id:            tempId,
    shortcut:      norm,
    _normShortcut: norm, // #128 cache
    text:          text.trim(),
    scope,
    agentEmail:    scope === 'agent' ? null : null, // server pins this for agent rows
    updatedAt:     Date.now(),
    _pending:      true,
  };

  // Step 1: optimistic commit
  $quickReplies.set(_sorted([optimistic, ...before]));
  _save(getAccountId(), [optimistic, ...before]);

  // Step 2: server create
  try {
    const res = await api.createQuickReply({ shortcut: optimistic.shortcut, text: optimistic.text, scope });
    const saved = res?.data || res;
    if (!saved?.id) throw new Error('Server did not return an id');

    // Step 3: reconcile — swap tempId for the real server id and clear
    // the pending flag. We snapshot fresh store state here (not `before`)
    // in case another optimistic row has been added in parallel.
    const reconciled = {
      id:            saved.id,
      shortcut:      saved.shortcut || optimistic.shortcut,
      _normShortcut: normalizeShortcut(saved.shortcut || optimistic.shortcut), // #128 cache
      text:          saved.text     || optimistic.text,
      scope:         saved.scope    || scope,
      agentEmail:    saved.agent_email || null,
      updatedAt:     saved.updated_at ? Date.parse(saved.updated_at) : optimistic.updatedAt,
    };
    const patched = $quickReplies.get().map(r => (r.id === tempId ? reconciled : r));
    const sorted  = _sorted(patched);
    $quickReplies.set(sorted);
    _save(getAccountId(), sorted);
    return reconciled;
  } catch (e) {
    // Step 4: rollback — remove the optimistic row. We filter by tempId
    // (not by restoring `before`) so any concurrent successful inserts
    // made during the in-flight window are preserved.
    const rolled = $quickReplies.get().filter(r => r.id !== tempId);
    $quickReplies.set(_sorted(rolled));
    _save(getAccountId(), rolled);
    throw new Error(e.message || 'Could not save');
  }
}

/**
 * Update an existing reply by id. Server-scoped via PATCH
 * /quick-replies/{id} → WHERE account_id = session.account_id. On a
 * 404 (row missing or cross-account) the proxy returns {error:
 * 'Quick reply not found'} and we rollback the optimistic state.
 *
 * Hard-guards against tmp_* ids: a row that hasn't finished its initial
 * create cannot be edited — the caller must wait for the reconcile. The
 * UI shouldn't render edit affordances on pending rows, but this guard
 * catches any path that tries anyway.
 */
export async function updateQuickReply(id, { shortcut, text }) {
  if (_isTempId(id)) {
    throw new Error('Saving — try again in a moment');
  }
  const before = $quickReplies.get();
  const idx = before.findIndex(r => r.id === id);
  if (idx < 0) throw new Error('Quick reply not found');

  const nextShortcut = shortcut !== undefined ? normalizeShortcut(shortcut) : before[idx].shortcut;
  const nextText     = text !== undefined ? String(text).trim() : before[idx].text;
  if (!nextShortcut) throw new Error('Shortcut is required');
  if (!nextText)     throw new Error('Reply text is required');
  // #170 — conflict check stays within the row's own scope.
  const rowScope = before[idx].scope || 'account';
  if (_shortcutConflict(before, nextShortcut, id, rowScope)) {
    throw new Error(`"/${nextShortcut}" is already in use ${rowScope === 'account' ? 'as a shared reply' : 'in your personal replies'}`);
  }

  const updated = {
    ...before[idx],
    shortcut:      nextShortcut,
    _normShortcut: nextShortcut, // #128 cache — already normalized above
    text:          nextText,
    updatedAt:     Date.now(),
  };
  const next    = _sorted(before.map(r => (r.id === id ? updated : r)));
  $quickReplies.set(next);
  _save(getAccountId(), next);

  try {
    const res = await api.updateQuickReply(id, { shortcut: updated.shortcut, text: updated.text });
    const saved = res?.data || res;
    // Patch the store with the server's authoritative timestamp so
    // subsequent sorts reflect the real update time, not the client
    // clock (which may drift).
    if (saved?.updated_at) {
      const withServer = $quickReplies.get().map(r =>
        r.id === id ? { ...r, updatedAt: Date.parse(saved.updated_at) } : r
      );
      const sorted = _sorted(withServer);
      $quickReplies.set(sorted);
      _save(getAccountId(), sorted);
    }
    return updated;
  } catch (e) {
    $quickReplies.set(_sorted(before));
    _save(getAccountId(), before);
    throw new Error(e.message || 'Could not update');
  }
}

/**
 * Remove a reply by id. Optimistic filter + DELETE /quick-replies/{id}
 * (server soft-deletes, scoped to account). Blocks on tmp_* ids — a
 * row that hasn't finished its initial create cannot be deleted until
 * the reconcile lands.
 */
export async function removeQuickReply(id) {
  if (_isTempId(id)) {
    throw new Error('Saving — try again in a moment');
  }
  const before = $quickReplies.get();
  const next = before.filter(r => r.id !== id);
  $quickReplies.set(_sorted(next));
  _save(getAccountId(), next);

  try {
    await api.deleteQuickReply(id);
  } catch (e) {
    // Rollback on failure so the row reappears and the user can retry
    $quickReplies.set(_sorted(before));
    _save(getAccountId(), before);
    throw new Error(e.message || 'Could not delete');
  }
}

// ── Composer matcher ───────────────────────────────────────────────────────

/**
 * Scan a composer text value for a /slash command prefix and return the
 * list of matching quick replies. Only fires when the cursor (passed as
 * `caret`, defaulting to full length) is inside a token that starts with
 * `/` at the beginning of the input OR right after whitespace/newline.
 *
 * Returns { token, matches } where `token` is the user-typed portion
 * (without the slash) and `matches` is up to 6 replies whose shortcut
 * starts with the token. Empty matches if no valid slash token under the
 * cursor.
 */
export function matchQuickReplyCommand(text, caret) {
  if (!text) return { token: null, matches: [] };
  const pos = typeof caret === 'number' ? caret : text.length;

  // Walk back from caret to find the start of the current word
  let start = pos;
  while (start > 0 && !/[\s]/.test(text[start - 1])) start -= 1;

  const word = text.slice(start, pos);
  if (!word.startsWith('/')) return { token: null, matches: [] };

  const token = normalizeShortcut(word);
  const list  = $quickReplies.get();
  const matches = list
    .filter(r => r.shortcut.startsWith(token))
    .slice(0, 6);

  return { token, matches, wordStart: start, wordEnd: pos };
}
